import { Suspense, useEffect } from 'react'
import { BrowserRouter, Routes, Route, Navigate } from 'react-router'
import { useAuthStore } from '@carouselgg/auth/store'
import { ProtectedRoute } from '@carouselgg/auth/protected-route'
import { AppLayout } from './components/AppLayout'
import { PublicLayout } from './components/PublicLayout'
import { DiscoveryLayout } from './components/DiscoveryLayout'
import { ComplianceModal } from './components/ComplianceModal'
import { NotificationProvider } from './context/NotificationContext'
import { HomePage } from './pages/HomePage'
import { LoggedOutHome } from './pages/LoggedOutHome'
import { NotificationsPage } from './pages/NotificationsPage'
import { MessageThreadPage } from './pages/MessageThreadPage'
import { NotificationDetailPage } from './pages/NotificationDetailPage'
import { NotificationSettingsPage } from './pages/NotificationSettingsPage'
import { TeamsClubsPage } from './pages/account/TeamsClubsPage'
import { CompetitionsPage } from './pages/account/CompetitionsPage'
import { AccountOverviewPage } from './pages/account/AccountOverviewPage'
import { SettingsRedirect } from './components/hub/SettingsRedirect'
import { AppNotFound } from './pages/AppNotFound'
import { PrivacyPage } from './pages/PrivacyPage'
import { TermsPage } from './pages/TermsPage'
import { CreateOrgPage } from './pages/CreateOrgPage'
import { NewsPage } from './pages/NewsPage'
import { ArticlePage } from './pages/ArticlePage'
import { CompetePage } from './pages/CompetePage'
import { OrgsPage } from './pages/OrgsPage'
import { AudiencePage } from './pages/audience/AudiencePage'
import { InvitePage } from './pages/InvitePage'
import { JoinPage } from './pages/JoinPage'
import { CheckInPage } from './pages/CheckInPage'
import { PassPage } from './pages/PassPage'
import { MyPassesPage } from './pages/MyPassesPage'
import { HowItWorksPage } from './pages/HowItWorksPage'
import { TrustPage } from './pages/trust/TrustPage'
import { TrustScoreSetupRedirect } from './pages/trust/TrustScoreSetupRedirect'
import { RemoteLoader } from './RemoteLoader'
import { Loading } from './Loading'
import {
  IdentityLogin,
  IdentitySignup,
  IdentityWelcome,
  IdentityLinkDone,
  IdentityProfileSettings,
  IdentitySettingsConnections,
  IdentityLegacyConnectionsRedirect,
  IdentitySettingsCalendars,
  IdentitySettingsSignIn,
  IdentityProfile,
  IdentitySearch,
  IdentityVerifyEmail,
  IdentityEmailChangeUndo,
  IdentityForgotPassword,
  IdentityResetPassword,
  IdentityOAuthCallback,
  IdentityProfileLayout,
  IdentityPlayerStats,
  IdentityMatchScorecard,
} from './remotes'

function LazyPage({ Component }: { Component: React.LazyExoticComponent<React.ComponentType> }) {
  return (
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  )
}

export function App() {
  const loadUser = useAuthStore((s) => s.loadUser)

  useEffect(() => {
    loadUser()
  }, [loadUser])

  return (
    <BrowserRouter>
      <ComplianceModal />
      <Routes>
        {/* Auth flow pages — full-page, no app shell, no notification provider */}
        <Route path="/login" element={<LazyPage Component={IdentityLogin} />} />
        <Route path="/signup" element={<LazyPage Component={IdentitySignup} />} />
        <Route path="/verify-email" element={<LazyPage Component={IdentityVerifyEmail} />} />
        <Route path="/email-change/undo" element={<LazyPage Component={IdentityEmailChangeUndo} />} />
        <Route path="/forgot-password" element={<LazyPage Component={IdentityForgotPassword} />} />
        <Route path="/reset-password" element={<LazyPage Component={IdentityResetPassword} />} />
        <Route path="/auth/callback" element={<LazyPage Component={IdentityOAuthCallback} />} />
        {/* Where a link-an-account window lands; its opener reads the result and closes it. */}
        <Route path="/auth/linked" element={<LazyPage Component={IdentityLinkDone} />} />
        {/* The optional welcome steps after a new player's first login. Full-page like
            the auth flow: they're the step between signing in and the app. */}
        <Route path="/welcome/*" element={<ProtectedRoute><LazyPage Component={IdentityWelcome} /></ProtectedRoute>} />

        {/* App shell pages — wrapped in NotificationProvider */}
        <Route element={
          <NotificationProvider>
            <AppLayout />
          </NotificationProvider>
        }>
          {/* Admin + org routes render inside AppLayout so error states get the shell.
              Their own layouts (AdminLayout / OrgLayout) overlay the shell when they load. */}
          <Route path="/org/:slug/manage/*" element={<RemoteLoader name="admin" />} />
          <Route path="/org/:slug/*" element={<RemoteLoader name="org" />} />
          {/* Main app dashboard (the home everyone lands on after login). */}
          <Route path="/dashboard" element={<HomePage />} />
          <Route path="/invites/:token" element={<InvitePage />} />
          <Route path="/join/:code" element={<JoinPage />} />
          {/* What a venue's check-in QR opens. Public: a stranger at the door
              needs to see how to get in before being asked to sign in. */}
          <Route path="/check-in/:token" element={<CheckInPage />} />
          {/* A player's check-in pass, scanned with a plain phone camera. */}
          <Route path="/pass/:payload" element={<PassPage />} />
          {/* The player's own passes, for anyone checking in without the app. */}
          <Route path="/passes" element={
            <ProtectedRoute>
              <MyPassesPage />
            </ProtectedRoute>
          } />
          <Route path="/u/:username" element={<LazyPage Component={IdentityProfileLayout} />}>
            <Route index element={<LazyPage Component={IdentityProfile} />} />
            <Route path="stats/:game?/:mode?" element={<LazyPage Component={IdentityPlayerStats} />} />
            <Route path="match/:externalMatchId" element={<LazyPage Component={IdentityMatchScorecard} />} />
          </Route>
          <Route path="/settings" element={
            <ProtectedRoute>
              <AccountOverviewPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/profile" element={
            <ProtectedRoute>
              <LazyPage Component={IdentityProfileSettings} />
            </ProtectedRoute>
          } />
          <Route path="/settings/connections" element={
            <ProtectedRoute>
              <LazyPage Component={IdentitySettingsConnections} />
            </ProtectedRoute>
          } />
          {/* Old homes of the Connections page; keeps ?linked= from OAuth returns. */}
          <Route path="/settings/accounts" element={<LazyPage Component={IdentityLegacyConnectionsRedirect} />} />
          <Route path="/settings/accounts/:tab" element={<LazyPage Component={IdentityLegacyConnectionsRedirect} />} />
          <Route path="/settings/phone" element={<SettingsRedirect to="/settings/sign-in" section="phone" />} />
          <Route path="/settings/team-contact" element={<SettingsRedirect to="/settings/sign-in" section="team-contact" />} />
          <Route path="/settings/calendars" element={
            <ProtectedRoute>
              <LazyPage Component={IdentitySettingsCalendars} />
            </ProtectedRoute>
          } />
          <Route path="/settings/security" element={<SettingsRedirect to="/settings/sign-in" section="security" />} />
          {/* Email & sign-in took over Security, Phone, Team contact and Delete account. */}
          <Route path="/settings/sign-in" element={
            <ProtectedRoute>
              <LazyPage Component={IdentitySettingsSignIn} />
            </ProtectedRoute>
          } />
          <Route path="/settings/account" element={<SettingsRedirect to="/settings/sign-in" section="delete-account" />} />
          <Route path="/search" element={
            <ProtectedRoute>
              <LazyPage Component={IdentitySearch} />
            </ProtectedRoute>
          } />
          <Route path="/create-org" element={
            <ProtectedRoute>
              <CreateOrgPage />
            </ProtectedRoute>
          } />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/notifications/:id" element={<NotificationDetailPage />} />
          <Route path="/messages/:threadId" element={
            <ProtectedRoute>
              <MessageThreadPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/notifications" element={
            <ProtectedRoute>
              <NotificationSettingsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/teams" element={
            <ProtectedRoute>
              <TeamsClubsPage />
            </ProtectedRoute>
          } />
          <Route path="/settings/memberships" element={<SettingsRedirect to="/settings/teams" />} />
          <Route path="/settings/competitions" element={
            <ProtectedRoute>
              <CompetitionsPage />
            </ProtectedRoute>
          } />
          {/* Your Carousel score and each game's, and how to raise them. */}
          <Route path="/trust" element={
            <ProtectedRoute>
              <TrustPage />
            </ProtectedRoute>
          } />
          <Route path="/trust/:gameSlug" element={
            <ProtectedRoute>
              <TrustPage />
            </ProtectedRoute>
          } />
          {/* The old "Build Your Identity" page; /trust replaced it. */}
          <Route path="/trust-score-setup" element={<TrustScoreSetupRedirect />} />
          <Route path="*" element={<AppNotFound />} />
        </Route>

        {/* The marketing homepage — marketing chrome for everyone. */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LoggedOutHome />} />
        </Route>

        {/* Discovery pages, reachable from both the app sidebar and the
            marketing nav. DiscoveryLayout keeps whichever chrome the visitor
            arrived with, so neither entry point drops you out of its shell. */}
        <Route element={<DiscoveryLayout />}>
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          <Route path="/compete" element={<CompetePage />} />
          <Route path="/orgs" element={<OrgsPage />} />
          {/* One pitch page per org type — see pages/audience/content.ts. */}
          <Route path="/for/:audience" element={<AudiencePage />} />

          {/* `/leagues` listed open *seasons*, not leagues, and duplicated
              `/compete`. `/universities` was a directory of university-typed
              orgs, which `/orgs?type=university` now is; the name belongs to
              the pitch page. Both kept as redirects for existing links. */}
          <Route path="/leagues" element={<Navigate to="/compete" replace />} />
          <Route path="/universities" element={<Navigate to="/for/universities" replace />} />
          <Route path="/news" element={<NewsPage />} />
          <Route path="/news/:slug" element={<ArticlePage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
        </Route>
      </Routes>
    </BrowserRouter>
  )
}
